'use client'
import type React from 'react'
import { motion, type Variants } from 'framer-motion'
import { ServiceCard } from './ServiceCard'

const SPECIALIZED = [
  {
    id: 'drayage',
    title: 'Drayage',
    description:
      'Short-haul port and rail operations. Seamless container retrieval and delivery from major Northeast terminals, including the Port of Newark and Elizabeth, directly to your distribution center.',
  },
  {
    id: 'white-glove',
    title: 'White Glove Services',
    description:
      'Premium handling for high-value or fragile freight. Specialized inside delivery, unpacking, and careful positioning for sensitive medical equipment, electronics, and specialized goods.',
  },
  {
    id: 'cross-docking',
    title: 'Cross Docking',
    description:
      'Accelerated freight transfer. We unload inbound shipments and move them directly into outbound trailers, minimizing warehousing costs and slashing transit times for final-mile delivery.',
  },
  {
    id: 'team-drivers',
    title: 'Team Drivers',
    description:
      'Expedited transit for time-critical freight. Dual-driver teams keep your load moving 24/7 without mandatory HOS layovers, effectively cutting coast-to-coast transit times in half.',
  },
]

const svgProps = {
  width: 48,
  height: 48,
  viewBox: '0 0 40 40',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  className: 'text-[color:var(--accent)]',
}

const ICONS: Record<string, React.ReactElement> = {
  drayage: (
    <svg {...svgProps}>
      <rect x="3" y="8" width="30" height="17" rx="1.5" />
      <line x1="11" y1="8" x2="11" y2="25" />
      <line x1="20" y1="8" x2="20" y2="25" />
      <line x1="29" y1="8" x2="29" y2="25" />
      <line x1="3" y1="25" x2="2" y2="30" />
      <line x1="33" y1="25" x2="34" y2="30" />
      <line x1="2" y1="30" x2="34" y2="30" />
      <circle cx="9" cy="33" r="3" />
      <circle cx="28" cy="33" r="3" />
    </svg>
  ),
  'white-glove': (
    <svg {...svgProps}>
      <rect x="6" y="14" width="26" height="18" rx="1.5" />
      <rect x="6" y="9" width="26" height="7" rx="1" />
      <line x1="19" y1="9" x2="19" y2="32" />
      <path d="M19 9 C19 9 12 5 11 10 C10 14 19 12 19 12" />
      <path d="M19 9 C19 9 26 5 27 10 C28 14 19 12 19 12" />
    </svg>
  ),
  'cross-docking': (
    <svg {...svgProps}>
      <rect x="2" y="9" width="14" height="10" rx="1" />
      <rect x="24" y="21" width="14" height="10" rx="1" />
      <rect x="14" y="15" width="12" height="10" rx="1" />
      <polyline points="22,15 26,20 22,25" />
      <polyline points="18,15 14,20 18,25" />
    </svg>
  ),
  'team-drivers': (
    <svg {...svgProps}>
      <circle cx="20" cy="20" r="14" />
      <circle cx="20" cy="20" r="4" />
      <line x1="20" y1="6" x2="20" y2="16" />
      <line x1="7.9" y1="27" x2="16.3" y2="22" />
      <line x1="32.1" y1="27" x2="23.7" y2="22" />
    </svg>
  ),
}

const cardGridVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 0.2 } },
}

export function SpecializedSection() {
  return (
    <section id="specialized" className="relative overflow-hidden py-28">
      <div className="absolute inset-0 bg-[#0f172a]/90" />

      <div className="relative z-10 page-container">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          <p className="text-sm font-semibold tracking-widest uppercase text-[color:var(--accent)] opacity-80 mb-3">
            Specialized Capabilities
          </p>
          <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight">
            Specialized Logistics Solutions
          </h2>
          <p className="mt-4 text-white/70 text-lg max-w-2xl mx-auto leading-relaxed">
            Beyond standard capacity, we offer specialized handling and expedited routing for complex supply chains.
          </p>
        </motion.div>

        <motion.ul
          className="grid grid-cols-1 md:grid-cols-2 gap-6 list-none p-0"
          variants={cardGridVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
        >
          {SPECIALIZED.map(({ id, title, description }, i) => (
            <ServiceCard
              key={id}
              id={id}
              title={title}
              description={description}
              icon={ICONS[id]}
              index={i + 3}
            />
          ))}
        </motion.ul>
      </div>
    </section>
  )
}
